/* LAKIRAS — theme.js
   Lightweight, dependency-free. Every feature degrades gracefully without JavaScript. */
(() => {
  'use strict';

  const LK = (window.LK = window.LK || {});
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const animOn = () => document.documentElement.classList.contains('anim-on') && !reduceMotion.matches;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, ms = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  LK.announce = (msg) => {
    const live = $('[data-live-region]');
    if (live) { live.textContent = ''; setTimeout(() => (live.textContent = msg), 50); }
  };

  /* ---------------------------------------------------------------------
     Money
  --------------------------------------------------------------------- */
  LK.formatMoney = (cents, format = LK.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = (n, precision, thousands = ',', decimal = '.') => {
      if (isNaN(n) || n == null) return '0';
      const num = (n / 100).toFixed(precision);
      const parts = num.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = fmt(cents, 2, ' ', '.'); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(match[0], value);
  };

  /* ---------------------------------------------------------------------
     Drawers (menu, search, bag) — accessible dialogs with focus trap
  --------------------------------------------------------------------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let activeDrawer = null;
  let lastFocus = null;

  const backdropFor = (drawer) => {
    const prev = drawer.previousElementSibling;
    return prev && prev.classList.contains('drawer-backdrop') ? prev : null;
  };

  LK.openDrawer = (id, opener) => {
    const drawer = document.getElementById(id);
    if (!drawer) return false;
    if (activeDrawer && activeDrawer !== drawer) LK.closeDrawer(activeDrawer, false);
    lastFocus = opener || document.activeElement;
    activeDrawer = drawer;
    drawer.classList.add('is-open');
    const bd = backdropFor(drawer);
    if (bd) bd.classList.add('is-visible');
    document.documentElement.style.overflow = 'hidden';
    $$(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    setTimeout(() => {
      const target = $('input[type="search"]', drawer) || $(FOCUSABLE, drawer) || drawer;
      target.focus({ preventScroll: true });
    }, 60);
    return true;
  };

  LK.closeDrawer = (drawer = activeDrawer, restore = true) => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    const bd = backdropFor(drawer);
    if (bd) bd.classList.remove('is-visible');
    document.documentElement.style.overflow = '';
    $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (restore && lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    if (drawer === activeDrawer) activeDrawer = null;
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      LK.openDrawer(opener.dataset.drawerOpen, opener);
      return;
    }
    const cartOpener = e.target.closest('[data-cart-open]');
    if (cartOpener && LK.cartType === 'drawer' && document.getElementById('CartDrawer')) {
      e.preventDefault();
      LK.openDrawer('CartDrawer', cartOpener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      const drawer = closer.closest('[data-drawer]') || (closer.nextElementSibling && closer.nextElementSibling.matches('[data-drawer]') ? closer.nextElementSibling : activeDrawer);
      LK.closeDrawer(drawer);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!activeDrawer) return;
    if (e.key === 'Escape') { LK.closeDrawer(); return; }
    if (e.key === 'Tab') {
      const items = $$(FOCUSABLE, activeDrawer).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------------------------------------------------------------------
     Header: transparent → solid, hide on scroll down
  --------------------------------------------------------------------- */
  class LkHeader extends HTMLElement {
    connectedCallback() {
      this.overlay = this.dataset.overlay === 'true';
      this.mode = this.dataset.sticky;
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.addEventListener('mouseenter', () => this.overlay && this.classList.add('is-hover'));
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    onScroll() {
      const y = window.scrollY;
      const h = this.offsetHeight;
      if (this.overlay) this.classList.toggle('is-solid', y > 8 && this.mode !== 'none');
      if (this.mode === 'scroll_up') {
        const down = y > this.lastY;
        this.classList.toggle('is-hidden', down && y > h * 2 && !activeDrawer);
      }
      this.lastY = y;
    }
  }
  customElements.define('lk-header', LkHeader);

  /* Announcement rotation */
  class LkAnnouncement extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2) return;
      this.i = 0;
      const ms = (parseInt(this.dataset.interval, 10) || 6) * 1000;
      this.timer = setInterval(() => this.next(), ms);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('focusin', () => clearInterval(this.timer));
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      this.items[this.i].classList.remove('is-active');
      this.i = (this.i + 1) % this.items.length;
      this.items[this.i].classList.add('is-active');
    }
  }
  customElements.define('lk-announcement', LkAnnouncement);

  /* ---------------------------------------------------------------------
     Entrance animations, lazy video, parallax
  --------------------------------------------------------------------- */
  let animObserver;
  const initAnimations = (root = document) => {
    const scopes = $$('[data-animate-scope]', root);
    scopes.forEach((scope) => {
      const els = $$('[data-anim]', scope);
      if (scope.dataset.animateScope === 'none' || !animOn()) {
        els.forEach((el) => el.classList.add('is-in'));
        return;
      }
      els.forEach((el, i) => { if (!el.style.getPropertyValue('--i')) el.style.setProperty('--i', Math.min(i, 5)); });
      if (!animObserver) {
        animObserver = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) { en.target.classList.add('is-in'); animObserver.unobserve(en.target); }
          });
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      }
      els.forEach((el) => animObserver.observe(el));
    });
    // Media scale-in (scoped to "scale" sections)
    $$('[data-animate-scope="scale"] [data-anim-media]', root).forEach((el) => {
      if (!animOn()) return el.classList.add('is-in');
      animObserver && animObserver.observe(el);
    });
  };

  let videoObserver;
  const pickSrc = (v) => {
    if (isMobile()) {
      if (v.dataset.srcMobile) return v.dataset.srcMobile;
      return null; // mobile has its own image or nothing better: keep the poster
    }
    return v.dataset.src || v.dataset.srcMobile || null;
  };
  const initVideos = (root = document) => {
    const videos = $$('video[data-lazy-video]', root);
    if (!videos.length) return;
    if (!videoObserver) {
      videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const v = en.target;
          if (en.isIntersecting) {
            if (!v.dataset.loaded) {
              const src = pickSrc(v);
              if (!src) return;
              v.src = src;
              v.dataset.loaded = '1';
              v.addEventListener('playing', () => {
                v.classList.add('is-playing');
                const t = v.parentElement && $('[data-video-toggle]', v.parentElement);
                if (t) t.hidden = false;
              }, { once: true });
            }
            if (!v.dataset.userPaused) v.play().catch(() => {});
          } else if (v.dataset.loaded) {
            v.pause();
          }
        });
      }, { rootMargin: '200px 0px', threshold: 0.01 });
    }
    videos.forEach((v) => {
      if (reduceMotion.matches) return; // poster only
      videoObserver.observe(v);
    });
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-video-toggle]');
    if (!btn) return;
    const v = $('video', btn.parentElement);
    if (!v) return;
    if (v.paused) { v.play(); delete v.dataset.userPaused; btn.setAttribute('aria-pressed', 'false'); }
    else { v.pause(); v.dataset.userPaused = '1'; btn.setAttribute('aria-pressed', 'true'); }
  });

  const parallaxEls = new Set();
  let ticking = false;
  const runParallax = () => {
    const vh = window.innerHeight;
    parallaxEls.forEach((el) => {
      const box = (el.closest('.media') || el.parentElement).getBoundingClientRect();
      if (box.bottom < -100 || box.top > vh + 100) return;
      const k = parseFloat(el.closest('[data-parallax-strength]')?.dataset.parallaxStrength || '0.12');
      const offset = (box.top + box.height / 2 - vh / 2) * -k;
      el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0) scale(${1 + k * 1.6})`;
    });
    ticking = false;
  };
  const initParallax = (root = document) => {
    if (!animOn()) return;
    $$('[data-parallax]', root).forEach((el) => parallaxEls.add(el));
    if (parallaxEls.size) runParallax();
  };
  window.addEventListener('scroll', () => {
    if (!parallaxEls.size || ticking) return;
    ticking = true;
    requestAnimationFrame(runParallax);
  }, { passive: true });

  /* ---------------------------------------------------------------------
     Scroll-driven stories: Discover the Fragrance + Sticky Story
     Observers toggle a data attribute; all visual change is CSS.
  --------------------------------------------------------------------- */
  class LkPhases extends HTMLElement {
    connectedCallback() {
      this.steps = $$('[data-step]', this);
      if (!this.steps.length) return;
      this.setStep(this.steps[0].dataset.step);
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) this.setStep(en.target.dataset.step); });
      }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
      this.steps.forEach((s) => this.io.observe(s));
    }
    disconnectedCallback() { this.io && this.io.disconnect(); }
    setStep(step) {
      if (this.dataset.active === step) return;
      this.dataset.active = step;
      this.steps.forEach((s) => s.classList.toggle('is-active', s.dataset.step === step));
      $$('[data-layer]', this).forEach((l) => l.classList.toggle('is-active', l.dataset.layer === step));
    }
  }
  customElements.define('lk-phases', LkPhases);

  /* Top / heart / base notes journey.
     The active stage is derived purely from how far the track has scrolled through the viewport:
     no wheel interception, no programmatic scrolling, identical forwards and backwards. */
  class LkJourney extends HTMLElement {
    connectedCallback() {
      this.panels = $$('.journey__panel', this);
      this.layers = $$('.journey__layer', this);
      this.rails = $$('[data-rail]', this);
      this.n = this.panels.length;
      if (!this.n) return;
      this.mq = window.matchMedia('(min-width: 990px)');
      this.active = -1;
      this.frame = null;
      this.onScroll = () => { if (this.visible && !this.frame) this.frame = requestAnimationFrame(() => this.update()); };
      this.onMode = () => this.setMode();
      this.io = new IntersectionObserver(([en]) => { this.visible = en.isIntersecting; if (this.visible) this.onScroll(); }, { rootMargin: '100px 0px' });
      this.io.observe(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.mq.addEventListener('change', this.onMode);
      reduceMotion.addEventListener('change', this.onMode);
      this.setMode();
    }
    disconnectedCallback() {
      this.io && this.io.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.mq && this.mq.removeEventListener('change', this.onMode);
      reduceMotion.removeEventListener('change', this.onMode);
    }
    setMode() {
      // Pin only where it helps: wide screens, motion allowed, more than one stage.
      this.pinned = this.mq.matches && !reduceMotion.matches && this.n > 1;
      this.classList.toggle('is-pinned', this.pinned);
      if (!this.pinned) { this.setActive(0); this.style.removeProperty('--p'); return; }
      this.update();
    }
    update() {
      this.frame = null;
      if (!this.pinned) return;
      const rect = this.getBoundingClientRect();
      const travel = this.offsetHeight - window.innerHeight;
      const progress = travel > 0 ? Math.min(1, Math.max(0, -rect.top / travel)) : 0;
      const pos = progress * this.n;
      const index = Math.min(this.n - 1, Math.floor(pos));
      this.style.setProperty('--p', progress.toFixed(4));
      this.style.setProperty('--sp', Math.min(1, pos - index).toFixed(4));
      this.setActive(index);
    }
    setActive(i) {
      if (i === this.active) return;
      this.active = i;
      this.dataset.active = String(i);
      this.dataset.tier = this.panels[i].dataset.tier;
      this.panels.forEach((p, k) => p.classList.toggle('is-active', k === i));
      this.layers.forEach((l, k) => l.classList.toggle('is-active', k === i));
      this.rails.forEach((r, k) => r.classList.toggle('is-active', k === i));
    }
  }
  customElements.define('lk-journey', LkJourney);

  /* You may also like: optional async recommendations + carousel arrows (shown only when the row overflows) */
  class LkYmal extends HTMLElement {
    connectedCallback() {
      if (this.dataset.url && !this.dataset.loaded) {
        this.dataset.loaded = '1';
        const io = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          fetch(this.dataset.url).then((r) => r.text()).then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('lk-ymal');
            if (!fresh || !fresh.querySelector('[data-ymal-item]')) return;
            const section = this.closest('.section');
            this.className = fresh.className;
            this.innerHTML = fresh.innerHTML;
            if (section) section.hidden = false;
            init(this);
            this.initNav();
          }).catch(() => {});
        }, { rootMargin: '400px 0px' });
        io.observe(this);
        return;
      }
      this.initNav();
    }
    initNav() {
      const track = $('[data-ymal-track]', this);
      const nav = $('[data-ymal-nav]', this);
      if (!track || !nav) return;
      const prev = $('[data-ymal-prev]', nav);
      const next = $('[data-ymal-next]', nav);
      const step = () => { const item = $('[data-ymal-item]', track); return item ? item.getBoundingClientRect().width + 32 : track.clientWidth * 0.8; };
      const sync = () => {
        const overflow = track.scrollWidth > track.clientWidth + 4;
        nav.hidden = !overflow;
        prev.disabled = track.scrollLeft <= 4;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', debounce(sync, 80), { passive: true });
      window.addEventListener('resize', debounce(sync, 150));
      sync();
    }
  }
  customElements.define('lk-ymal', LkYmal);

  /* Image reveal (scroll mode) */
  class LkReveal extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mode !== 'scroll' && this.dataset.mode !== 'both') return;
      this.onScroll = () => {
        const r = this.getBoundingClientRect();
        const vh = window.innerHeight;
        const p = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height * 0.5)));
        this.style.setProperty('--reveal', reduceMotion.matches ? (p > 0.5 ? 1 : 0) : p.toFixed(3));
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() { this.onScroll && window.removeEventListener('scroll', this.onScroll); }
  }
  customElements.define('lk-reveal', LkReveal);

  /* ---------------------------------------------------------------------
     Bag (AJAX cart via Section Rendering API)
  --------------------------------------------------------------------- */
  const Cart = (LK.cart = {
    sections: () => (document.getElementById('CartDrawer') ? ['cart-drawer'] : []),

    render(sections) {
      if (!sections) return;
      const html = sections['cart-drawer'];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cart-drawer-inner]');
        const current = $('[data-cart-drawer-inner]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        const count = doc.querySelector('[data-cart-count-value]');
        if (count) Cart.setCount(parseInt(count.textContent, 10) || 0);
      }
    },

    setCount(n) {
      $$('[data-cart-count]').forEach((el) => (el.textContent = n > 0 ? n : ''));
    },

    async post(url, body) {
      const drawer = $('[data-cart-drawer]');
      drawer && drawer.classList.add('is-loading');
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: Cart.sections(), sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || LK.strings.error);
        Cart.render(data.sections);
        if (typeof data.item_count === 'number') Cart.setCount(data.item_count);
        document.dispatchEvent(new CustomEvent('lk:cart:updated', { detail: data }));
        return data;
      } finally {
        drawer && drawer.classList.remove('is-loading');
      }
    },

    change(line, quantity) {
      return Cart.post(`${LK.routes.cartChange}.js`, { line: Number(line), quantity: Number(quantity) })
        .catch((err) => LK.announce(err.message));
    },
  });

  // Add to bag — progressive enhancement of native product forms
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form || LK.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"][name="add"]') || e.submitter;
    const errorEl = form.querySelector('[data-form-error]') || (form.id && document.querySelector(`[data-form-error="${form.id}"]`));
    if (errorEl) errorEl.hidden = true;
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    const fd = new FormData(form);
    Cart.sections().forEach((s) => fd.append('sections', s));
    fd.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(`${LK.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || LK.strings.error);
      Cart.render(data.sections);
      LK.announce(LK.strings.added);
      document.dispatchEvent(new CustomEvent('lk:cart:added', { detail: data }));
      LK.openDrawer('CartDrawer', btn);
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      else LK.announce(err.message);
    } finally {
      if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = btn.dataset.soldOut === 'true'; }
    }
  });

  // Quantity / remove inside the bag (drawer + page)
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-change]');
    if (step) {
      const wrap = step.closest('.qty');
      const input = wrap && $('input', wrap);
      if (!input) return;
      const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.qtyChange, 10));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = e.target.closest('[data-remove-line]');
    if (remove && remove.closest('[data-cart-drawer]')) {
      e.preventDefault();
      Cart.change(remove.dataset.removeLine, 0);
    }
  });
  document.addEventListener('change', debounce((e) => {
    const input = e.target.closest && e.target.closest('[data-qty-input]');
    if (input && input.closest('[data-cart-drawer]')) Cart.change(input.dataset.line, input.value);
    const note = e.target.closest && e.target.closest('[data-cart-note]');
    if (note) fetch(`${LK.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  }, 350));

  // Bag page: quantity changes submit the (non-JS compatible) form
  document.addEventListener('change', (e) => {
    const input = e.target.closest && e.target.closest('[data-qty-input]');
    const form = input && input.closest('form[data-cart-page-form]');
    if (form) debounce(() => form.submit(), 500)();
  });

  /* ---------------------------------------------------------------------
     Predictive search
  --------------------------------------------------------------------- */
  const initSearch = () => {
    const input = $('[data-predictive-input]');
    const results = $('[data-predictive-results]');
    if (!input || !results) return;
    let controller;
    input.addEventListener('input', debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      controller && controller.abort();
      controller = new AbortController();
      try {
        const url = `${LK.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url, { signal: controller.signal });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-predictive-content]');
        results.innerHTML = fresh ? fresh.innerHTML : '';
      } catch (err) { /* aborted */ }
    }, 280));
  };

  /* Localization auto-submit */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest && e.target.closest('[data-autosubmit]');
    if (sel) sel.form.submit();
  });

  /* ---------------------------------------------------------------------
     Init + Theme editor support
  --------------------------------------------------------------------- */
  const init = (root = document) => {
    initAnimations(root);
    initVideos(root);
    initParallax(root);
  };
  LK.init = init;

  const ready = () => { init(); initSearch(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();

  document.addEventListener('shopify:section:load', (e) => { init(e.target); if (e.target.querySelector('[data-predictive-input]')) initSearch(); });
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector('#CartDrawer')) LK.openDrawer('CartDrawer');
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    if (e.target.querySelector('#CartDrawer')) LK.closeDrawer();
  });
})();
